import type {
  Filter,
  FilterToggle,
  FilterToggleValue,
  ProductListingPage,
} from "@decocms/apps-commerce/types";
import { PROD_URL_HTTPS, sanitizeStoreUrl } from "~/utils/constants";
import { useCallback, useRef, useState } from "react";
import PriceRange from "./PriceRange";

interface Props {
  filters: ProductListingPage["filters"];
}

type FilterFamily = "categories" | "color" | "size" | "price";

const EN_TO_PT_MAPPING = {
  categories: "categoria",
  cor: "cor",
  "cor-filtro": "cor",
  tamanho: "tamanho",
};

const CATEGORY_KEYS = ["category-3", "category-2", "category-1", "categories"];
const COLOR_KEYS = ["cor-filtro", "cor"];
const SIZE_KEYS = ["tamanho"];
const PRICE_KEYS = ["price"];
const DEPARTMENTS_KEY = "departments";

const RESERVED_FILTER_KEYS = new Set([
  ...CATEGORY_KEYS,
  ...COLOR_KEYS,
  ...SIZE_KEYS,
  ...PRICE_KEYS,
  DEPARTMENTS_KEY,
  "brands",
  // "priceranges" não está mais em PRICE_KEYS (ver getFilterFamily), mas
  // precisa continuar reservada aqui: sem isso, o filtro legado dessa
  // chave passaria a ser elegível pro fallback de "categoria dinâmica"
  // (isDynamicCategoryFilter), que não é o comportamento correto pra ela.
  "priceranges",
]);

const FILTER_PRIORITY: Record<string, number> = {
  "category-3": 4,
  "category-2": 3,
  "category-1": 2,
  categories: 1,
  "cor-filtro": 2,
  cor: 1,
  tamanho: 1,
  price: 1,
};

const normalizeFilterKey = (key: string) => key.toLowerCase();

const hasVisibleValues = (filter: FilterToggle) =>
  filter.values.some((value) => value.quantity > 0 || value.selected);

const hasCategoryFilterUrl = (url: string) =>
  /filter\.category-\d+=/i.test(url);

const getFilterFamily = (key: string): FilterFamily | null => {
  const normalizedKey = normalizeFilterKey(key);

  if (CATEGORY_KEYS.includes(normalizedKey)) {
    return "categories";
  }

  if (COLOR_KEYS.includes(normalizedKey)) {
    return "color";
  }

  if (SIZE_KEYS.includes(normalizedKey)) {
    return "size";
  }

  if (PRICE_KEYS.includes(normalizedKey)) {
    return "price";
  }

  return null;
};

const isSearchFilterToggle = (filter: Filter): filter is FilterToggle =>
  filter["@type"] === "FilterToggle" &&
  getFilterFamily(filter.key) !== null;

const getFilterPriority = (key: string) =>
  FILTER_PRIORITY[normalizeFilterKey(key)] ?? 0;

const getFilterLabel = (key: string) => {
  const normalizedKey = normalizeFilterKey(key);

  if (CATEGORY_KEYS.includes(normalizedKey)) {
    return "categoria";
  }

  if (COLOR_KEYS.includes(normalizedKey)) {
    return "cor";
  }

  if (SIZE_KEYS.includes(normalizedKey)) {
    return "tamanho";
  }

  if (PRICE_KEYS.includes(normalizedKey)) {
    return "preço";
  }

  return EN_TO_PT_MAPPING[normalizedKey as keyof typeof EN_TO_PT_MAPPING] ??
    key;
};

// Ordem de exibição dos filtros na PLP: preço sempre logo após tamanho.
const FAMILY_ORDER: FilterFamily[] = ["categories", "color", "size", "price"];

const getFamilyOrder = (key: string) => {
  const family = getFilterFamily(key);
  const index = family ? FAMILY_ORDER.indexOf(family) : -1;

  return index === -1 ? FAMILY_ORDER.length : index;
};

const sortFilters = (filters: FilterToggle[]) =>
  [...filters].sort((a, b) =>
    getFamilyOrder(a.key) - getFamilyOrder(b.key) ||
    getFilterPriority(b.key) - getFilterPriority(a.key)
  );

const toCategoryFilter = (filter: FilterToggle): FilterToggle => ({
  ...filter,
  label: "Categories",
  key: "categories",
});

const isDynamicCategoryFilter = (filter: FilterToggle) => {
  const normalizedKey = normalizeFilterKey(filter.key);

  if (RESERVED_FILTER_KEYS.has(normalizedKey)) {
    return false;
  }

  return hasVisibleValues(filter) &&
    filter.values.every((value) => hasCategoryFilterUrl(value.url));
};

const selectCategoryFilter = (filters: ProductListingPage["filters"]) => {
  const standardCategoryFilters = filters
    .filter(isSearchFilterToggle)
    .filter((filter) => getFilterFamily(filter.key) === "categories")
    .filter(hasVisibleValues);

  if (standardCategoryFilters.length > 0) {
    return sortFilters(standardCategoryFilters)[0];
  }

  const dynamicCategoryFilters = filters
    .filter((filter): filter is FilterToggle =>
      filter["@type"] === "FilterToggle"
    )
    .filter(isDynamicCategoryFilter);

  if (dynamicCategoryFilters.length === 1) {
    return toCategoryFilter(dynamicCategoryFilters[0]);
  }

  const departmentsFilter = filters
    .filter((filter): filter is FilterToggle =>
      filter["@type"] === "FilterToggle"
    )
    .find((filter) =>
      normalizeFilterKey(filter.key) === DEPARTMENTS_KEY &&
      hasVisibleValues(filter)
    );

  if (departmentsFilter) {
    return toCategoryFilter(departmentsFilter);
  }

  return null;
};

const selectVisibleSearchFilters = (filters: ProductListingPage["filters"]) => {
  const bestFilters = new Map<FilterFamily, FilterToggle>();

  const categoryFilter = selectCategoryFilter(filters);

  if (categoryFilter) {
    bestFilters.set("categories", categoryFilter);
  }

  for (const filter of filters.filter(isSearchFilterToggle)) {
    if (!hasVisibleValues(filter)) {
      continue;
    }

    const family = getFilterFamily(filter.key);

    if (!family) {
      continue;
    }

    if (family === "categories") {
      continue;
    }

    const currentFilter = bestFilters.get(family);

    if (
      !currentFilter ||
      getFilterPriority(filter.key) > getFilterPriority(currentFilter.key)
    ) {
      bestFilters.set(family, filter);
    }
  }

  return sortFilters(Array.from(bestFilters.values()));
};

function PriceRangeFilter({ values }: { values: FilterToggleValue[] }) {
  const valuesArray = Object.entries(values);
  const url = valuesArray[0][1].url;

  const urlPrice = url
    .split("&")
    .slice(0, -1)
    .filter((r) => r.includes("filter.price"))[0]
    ?.split("=")[1]
    ?.split("%3A");
  const urlBrowser = url
    .split("&")
    .slice(0, -1)
    .filter((r) => !r.includes("filter.price"))
    .join("&");
  const rangeArray: number[] = [];

  valuesArray.map((value) => {
    const aux = value[1].value.split(":");
    const auxArr = aux.map((r) => parseInt(r));
    rangeArray.push(...auxArr);
  });
  rangeArray.sort((a, b) => a - b);
  const minRange = rangeArray[0];
  const maxRange = rangeArray[rangeArray.length - 1];

  const [currentMaxMin, setCurrentMaxMin] = useState({
    max: urlPrice ? parseInt(urlPrice[1]) : maxRange,
    min: urlPrice ? parseInt(urlPrice[0]) : minRange,
  });

  const timeOutIdRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const firstTimeRef = useRef(0);

  // onChange precisa ser estável entre renders: MultiRangeSlider (PriceRange)
  // tem um useEffect com onChange nas deps — se essa função for recriada a
  // cada render do componente pai (como era antes, uma arrow function
  // inline), o efeito reexecuta a cada render, chamando onChange de novo,
  // que nesse caso muda o estado (setCurrentMaxMin) e causa mais um render,
  // realimentando o ciclo. firstTime/timeOutId também eram variáveis locais
  // comuns (não useRef) — reiniciavam do zero a cada render, quebrando de
  // vez o debounce de 500ms que deveriam implementar (a condição
  // `firstTime > 0` nunca via o valor incrementado num render anterior).
  const onPriceChange = useCallback(
    (query: { min: number; max: number }) => {
      if (
        currentMaxMin.max != query.max ||
        currentMaxMin.min != query.min
      ) {
        if (firstTimeRef.current > 0) {
          clearTimeout(timeOutIdRef.current);
          timeOutIdRef.current = setTimeout(() => {
            setCurrentMaxMin({ max: query.max, min: query.min });
            globalThis.window.location.href = urlBrowser +
              "&filter.price=" +
              query.min +
              "%3A" +
              query.max;
          }, 500);
        }
        firstTimeRef.current++;
      }
    },
    [currentMaxMin, urlBrowser],
  );

  return (
    // No desktop o slider vive na barra de filtros de uma linha (altura ~65px),
    // então não pode reservar as 4rem + margem que ele usa no drawer mobile —
    // era isso que esticava a barra pra quase o dobro da altura desenhada.
    <div className="h-16 mt-8 mb-0 lg:h-[30px] lg:mt-0">
      <PriceRange
        darkBackground={true}
        min={minRange}
        max={maxRange}
        currentMin={currentMaxMin.min}
        currentMax={currentMaxMin.max}
        onChange={onPriceChange}
      />
    </div>
  );
}

function DesktopFilters({ filters }: { filters: FilterToggle[] }) {
  const visibleFilters = selectVisibleSearchFilters(filters);

  if (!visibleFilters.length) {
    return <div />;
  }

  // gap-x reduzido (era 15/25px): item Alta da lista do time, "filtros
  // pequenos e afastados no desktop" — menor gap aproxima os selects sem
  // aumentar a largura total da barra, então não reintroduz a quebra de
  // linha que o fix de f1374e9 (03/09) resolveu (aquele fix dependia dos
  // selects encolherem e da barra não crescer horizontalmente; um gap
  // MENOR nunca piora isso, só um gap ou selects MAIORES poderiam).
  return (
    <ul className="hidden lg:flex flex-nowrap items-center gap-x-[8px] xl:gap-x-[14px] min-w-0 flex-1">
      {visibleFilters.map(({ key, values }) => {
        if (!values || !values.length) {
          return null;
        }

        if (normalizeFilterKey(key) === "price") {
          return (
            <li key={key} className="flex flex-col gap-4 shrink-0">
              <PriceRangeFilter values={values} />
            </li>
          );
        }

        return (
          // flex-1 + min-w-0: os selects encolhem (até o limite de w-50) pra
          // barra inteira caber numa linha só em telas menores.
          <li key={key} className="flex-1 min-w-[90px] max-w-50">
            <select
              id={key}
              name={key}
              onChange={(e) => {
                if (e.currentTarget.value !== "default") {
                  window.location.href = e.currentTarget.value;
                }
              }}
              className="font-['Poppins'] text-[13px] font-normal w-full h-[34px] text-[#333333] px-2 xl:px-[15px] border-r-[10px] xl:border-r-[15px] border-[transparent] py-0 rounded cursor-pointer"
            >
              <option value="default">
                {getFilterLabel(key)}
              </option>
              {values
                .filter(({ label, quantity, selected }) =>
                  label && (quantity > 0 || selected)
                )
                .map(({ value, label, url, selected }) => (
                  <option
                    key={value}
                    value={sanitizeStoreUrl(url.split("&fmap=c")[0])}
                    selected={selected}
                  >
                    {label}
                  </option>
                ))}
            </select>
          </li>
        );
      })}
      <a
        className="p-2.5 px-1 xl:px-2.5 text-[#333333] text-[10px] font-['Poppins'] font-medium shrink-0 whitespace-nowrap"
        href={(() => {
          const pathname = globalThis?.location?.pathname;
          if (pathname) {
            const segments = pathname.split("/").filter(Boolean);
            return segments.length > 1
              ? `/${segments[0]}/${segments[1]}`
              : `/${segments[0]}`;
          }
          return "/";
        })()}
      >
        <span className="clean">limpar filtros</span>
      </a>
    </ul>
  );
}

function MobileFilterValues({
  values,
  filterName,
}: {
  values: FilterToggleValue[];
  filterName: string;
}) {
  // URL de assets estáticos — nunca deriva de globalThis.location: isso causa
  // mismatch de hidratação (SSR não tem `location`, cai no fallback; o
  // cliente usa a origem real do navegador, divergindo do servidor).
  const host = PROD_URL_HTTPS;

  return (
    <div className="collapse collapse-arrow border-b-[1px] border-solid border-[#F1F2E8]">
      {/* Sem `checked` (nem defaultChecked): antes esse checkbox era
          controlado como sempre marcado e sem onChange, então o grupo de
          filtro nunca colapsava de fato — renderizava sempre expandido no
          mobile (achado do time, 2026-08-26). Deixando não-controlado
          (desmarcado por padrão), cada grupo começa colapsado e o próprio
          clique no título/checkbox do DaisyUI abre/fecha normalmente. */}
      <input
        type="checkbox"
        aria-label={`Expandir ou retrair filtro de ${filterName}`}
        className="min-h-[41px]"
      />
      <p className="collapse-title m-0 min-h-[41px] uppercase py-[13px] px-5 font-['Poppins'] not-italic font-bold text-sm leading-[20px] text-black">
        {filterName}
      </p>
      <div className="collapse-content p-0">
        <ul>
          {values?.map((value) => (
            <li
              key={value.value}
              className={`py-[13px] px-6 flex items-center ${
                value.selected ? "bg-gray-200" : ""
              }`}
            >
              {filterName.toLowerCase() === "cor" && (
                <img
                  width={10}
                  height={10}
                  loading="lazy"
                  fetchPriority="low"
                  decoding="async"
                  className="w-2.5 h-2.5 shrink-0 border-0 rounded-full mr-2.5"
                  src={`${host}/arquivos/cor-sku-${
                    value.label
                      .toLowerCase()
                      .replace(/ *\([^)]*\) */g, "")
                  }.jpg`}
                />
              )}
              <a
                className="font-['Poppins'] not-italic font-normal text-sm uppercase text-black"
                href={sanitizeStoreUrl(value.url)}
              >
                {value.label}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function MobileFilters({ filters }: Props) {
  const visibleFilters = selectVisibleSearchFilters(filters);

  if (!visibleFilters.length) {
    return (
      <span className="text-center mt-7 font-['Poppins']">
        Os resultados atuais nao possuem filtros
      </span>
    );
  }

  return (
    <ul className="grow flex flex-col divide-y divide-base-200 bg-white">
      {visibleFilters.map((filter) => {
        const { key, values } = filter;

        if (normalizeFilterKey(key) === "price") {
          return (
            <li key={key} className="flex flex-col gap-4">
              <PriceRangeFilter values={values} />
            </li>
          );
        }

        return (
          <li key={key} className="flex flex-col gap-4">
            <MobileFilterValues
              values={values}
              filterName={getFilterLabel(key)}
            />
          </li>
        );
      })}
    </ul>
  );
}

export {
  DesktopFilters,
  isSearchFilterToggle,
  MobileFilters,
  selectVisibleSearchFilters,
};
